import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { parseRmtMessage, stringifyRmtMessage } from "@bi-mind/rmt-protocol";

type Session = {
  id: string | null;
  requestId: string;
  term: Terminal;
  fit: FitAddon;
  el: HTMLElement;
};

/** Hub yalnızca Keycloak modunu döndürür; eski kurulum uyarısı için string. */
type AuthConfig = {
  mode: string;
  loginUrl: string | null;
  logoutUrl: string | null;
  requiredRole: string | null;
};

let loginPath = "/auth/login";
let logoutPath = "/auth/logout";
let realmRoleHint: string | null = null;
/** Hub’a ulaşılamıyorsa veya uyumsuz yapılandırma. */
let hubUnavailable: string | null = null;

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function wsBrowserUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws?role=browser`;
}

function setStatus(text: string) {
  $("status")!.textContent = text;
}

let socket: WebSocket | null = null;
const sessions: Session[] = [];

function send(msg: Parameters<typeof stringifyRmtMessage>[0]) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(stringifyRmtMessage(msg));
  }
}

function removeSession(sess: Session) {
  if (sess.id) {
    send({ v: 1, type: "session_close", sessionId: sess.id, reason: "closed" });
  }
  sess.term.dispose();
  sess.el.remove();
  const idx = sessions.indexOf(sess);
  if (idx >= 0) sessions.splice(idx, 1);
}

function setShellBlocked(block: boolean) {
  const shell = $("app") as HTMLElement | null;
  if (!shell) return;
  shell.classList.toggle("shell-blocked", block);
}

function wallSubEl(): HTMLElement {
  return $("login-wall-sub")!;
}

function setWallSub(text: string, variant: "info" | "alert" = "info") {
  const el = wallSubEl();
  el.textContent = text;
  el.classList.toggle("alert", variant === "alert");
}

async function revertToLoginAfterHubReject(reasonLine: string) {
  if (hubUnavailable) return;
  setWallSub(reasonLine, "alert");
  await reconcileAuthChrome();
}

async function fetchSession(): Promise<{
  authenticated: boolean;
  sub?: string;
}> {
  if (hubUnavailable) return { authenticated: false };
  try {
    const res = await fetch("/api/auth/session", { credentials: "include" });
    if (!res.ok) return { authenticated: false };
    const j = (await res.json()) as {
      authenticated?: boolean;
      sub?: string;
    };
    return { authenticated: Boolean(j.authenticated), sub: j.sub };
  } catch {
    return { authenticated: false };
  }
}

function applyRoleHint() {
  const el = $("login-wall-role");
  if (!el) return;
  el.textContent = realmRoleHint
    ? `Gerekli realm rolü: «${realmRoleHint}».`
    : "";
}

async function reconcileAuthChrome() {
  const connectBtn = $("connect") as HTMLButtonElement;
  const newBtn = $("new-session") as HTMLButtonElement;
  const wall = $("kc-login-wall");
  const logoutToolbar = $("btn-toolbar-logout") as HTMLButtonElement;
  const btnLogin = $("btn-wall-login") as HTMLButtonElement | null;
  const btnLogout = $("btn-wall-logout") as HTMLButtonElement | null;

  if (!connectBtn || !newBtn || !wall || !logoutToolbar) return;

  if (hubUnavailable) {
    wall.classList.remove("hidden");
    setShellBlocked(true);
    logoutToolbar.classList.add("hidden");
    connectBtn.disabled = true;
    newBtn.disabled = true;
    if (btnLogin) btnLogin.disabled = true;
    if (btnLogout) btnLogout.disabled = true;
    setWallSub(hubUnavailable, "alert");
    setStatus("Hub yapılandırması eksik veya sunucuya ulaşılamıyor.");
    applyRoleHint();
    return;
  }

  if (btnLogin) btnLogin.disabled = false;
  if (btnLogout) btnLogout.disabled = false;

  applyRoleHint();

  const sess = await fetchSession();

  if (!sess.authenticated) {
    wall.classList.remove("hidden");
    setShellBlocked(true);
    logoutToolbar.classList.add("hidden");
    connectBtn.disabled = true;
    newBtn.disabled = true;

    if (!wallSubEl().classList.contains("alert")) {
      setWallSub(
        "Giriş yapılmadı. Aşağıdaki düğme sizi güvenli Keycloak sayfasına götürür."
      );
      setStatus("Giriş gerekli");
    }
    return;
  }

  wall.classList.add("hidden");
  setShellBlocked(false);
  logoutToolbar.classList.remove("hidden");
  connectBtn.disabled = false;
  newBtn.disabled = !(socket?.readyState === WebSocket.OPEN);

  wallSubEl().classList.remove("alert");

  const who = sess.sub ? ` (${sess.sub.slice(0, 36)}…)` : "";
  setWallSub("", "info");

  const subShort = sess.sub ? sess.sub.slice(0, 20) + "…" : "hesap";
  setStatus(`${subShort}${who} ile giriş yapıldı — Bağlan'a basın.`);
}

function createSessionHost(requestId: string): Session {
  const wrap = document.createElement("div");
  wrap.className = "session";
  const head = document.createElement("div");
  head.className = "session-head";
  head.innerHTML =
    '<span>Oturum</span><code>başlıyor…</code><button type="button" class="close-btn">Kapat</button>';
  const termEl = document.createElement("div");
  termEl.style.height = "calc(100% - 40px)";
  termEl.style.padding = "8px";
  wrap.appendChild(head);
  wrap.appendChild(termEl);
  $("term-host")?.appendChild(wrap);

  const term = new Terminal({
    convertEol: false,
    cursorBlink: true,
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: 14,
    theme: { background: "#010409", foreground: "#e6edf3" },
    disableStdin: true,
  });
  const fit = new FitAddon();
  term.loadAddon(fit);
  term.open(termEl);
  fit.fit();

  const closeBtn = head.querySelector(".close-btn") as HTMLButtonElement;
  const sess: Session = { id: null, requestId, term, fit, el: wrap };

  closeBtn.addEventListener("click", () => {
    removeSession(sess);
  });

  const ro = new ResizeObserver(() => {
    fit.fit();
    if (sess.id && socket && socket.readyState === WebSocket.OPEN) {
      send({
        v: 1,
        type: "resize",
        sessionId: sess.id,
        cols: term.cols,
        rows: term.rows,
      });
    }
  });
  ro.observe(wrap);

  term.onData((data) => {
    if (!sess.id) return;
    const bytes = new TextEncoder().encode(data);
    let binary = "";
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]!);
    }
    send({
      v: 1,
      type: "stdin",
      sessionId: sess.id,
      data: btoa(binary),
    });
  });

  return sess;
}

function attachSocketListeners(
  connectBtn: HTMLButtonElement,
  newBtn: HTMLButtonElement
) {
  if (!socket) return;

  socket.addEventListener("open", () => {
    setStatus("Bağlı — Yeni oturum açabilirsiniz.");
    connectBtn.disabled = true;
    newBtn.disabled = false;
  });

  socket.addEventListener("close", (ev) => {
    const code4401 =
      typeof ev.code === "number"
        ? ev.code === 4401
        : String(ev.code) === "4401";
    if (code4401) {
      void revertToLoginAfterHubReject(
        "Sunucu oturumu kabul etmedi (unauthorized). Keycloak’a tekrar giriş yapın."
      );
    } else {
      void reconcileAuthChrome();
    }
    connectBtn.disabled = false;
    newBtn.disabled = true;
    for (const s of [...sessions]) removeSession(s);
    socket = null;
  });

  socket.addEventListener("error", () => {
    setStatus("WebSocket ağ hatası");
  });

  socket.addEventListener("message", (ev) => {
    const msg = parseRmtMessage(String(ev.data));
    if (!msg) return;

    if (msg.type === "error") {
      const m = msg.message.toLowerCase();
      if (m.includes("unauthorized") || m.includes("forbidden role")) {
        void revertToLoginAfterHubReject(
          m.includes("forbidden role")
            ? "Yetkiniz yok (realm rolü). Yöneticiden doğru rolü isteyip tekrar giriş yapın."
            : "Oturum çereziniz hub tarafından kabul edilmedi. Tekrar «Keycloak ile giriş yap» deneyin."
        );
      } else setStatus(msg.message);
      return;
    }

    if (msg.type === "session_opened") {
      const sess = sessions.find((s) => s.requestId === msg.requestId);
      if (!sess || sess.id !== null) return;
      sess.id = msg.sessionId;
      const code = sess.el.querySelector("code");
      if (code) code.textContent = `${msg.sessionId.slice(0, 8)}…`;
      sess.term.options.disableStdin = false;
      sess.fit.fit();
      send({
        v: 1,
        type: "resize",
        sessionId: msg.sessionId,
        cols: sess.term.cols,
        rows: sess.term.rows,
      });
      return;
    }

    if (msg.type === "stdout") {
      const sess = sessions.find((s) => s.id === msg.sessionId);
      if (!sess) return;
      const bytes = Uint8Array.from(atob(msg.data), (c) => c.charCodeAt(0));
      sess.term.write(new TextDecoder().decode(bytes));
      return;
    }

    if (msg.type === "session_close") {
      const sess = sessions.find((s) => s.id === msg.sessionId);
      if (sess) removeSession(sess);
    }
  });
}

async function wireSocket() {
  const connectBtn = $("connect") as HTMLButtonElement;
  const newBtn = $("new-session") as HTMLButtonElement;

  await reconcileAuthChrome();
  const s = await fetchSession();

  if (hubUnavailable) return;

  if (!s.authenticated) {
    setWallSub(
      "Önce «Keycloak ile giriş yap» ile kurumsal hesabınızla oturum açın.",
      "alert"
    );
    return;
  }

  socket = new WebSocket(wsBrowserUrl());
  attachSocketListeners(connectBtn, newBtn);
}

function openSession() {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  const requestId = crypto.randomUUID();
  const sess = createSessionHost(requestId);
  sessions.push(sess);
  socket.send(
    stringifyRmtMessage({
      v: 1,
      type: "session_request",
      requestId,
      cols: sess.term.cols,
      rows: sess.term.rows,
    })
  );
}

function applyAuthUi(cfg: AuthConfig) {
  if (cfg.loginUrl) loginPath = cfg.loginUrl;
  if (cfg.logoutUrl) logoutPath = cfg.logoutUrl;
  realmRoleHint = cfg.requiredRole;
}

async function loadAuthConfig(): Promise<AuthConfig> {
  const res = await fetch("/api/auth/config", { credentials: "include" });
  if (!res.ok) throw new Error("auth config");
  return (await res.json()) as AuthConfig;
}

function wireWallAndToolbarLogout() {
  $("btn-wall-login")?.addEventListener("click", () => {
    if (hubUnavailable) return;
    wallSubEl().classList.remove("alert");
    window.location.assign(loginPath);
  });
  $("btn-wall-logout")?.addEventListener("click", () => {
    if (hubUnavailable) return;
    window.location.assign(logoutPath);
  });
  $("btn-toolbar-logout")?.addEventListener("click", () => {
    if (hubUnavailable) return;
    window.location.assign(logoutPath);
  });
}

async function init() {
  wireWallAndToolbarLogout();

  const params = new URLSearchParams(window.location.search);

  if (params.get("auth") === "forbidden") {
    setWallSub(
      "Hesap giriş yaptı ancak gerekli realm rolü atanmamış. Yöneticiyle görüşün.",
      "alert"
    );
  }

  try {
    const cfg = await loadAuthConfig();
    if (cfg.mode !== "keycloak") {
      hubUnavailable =
        "Bu arayüz yalnızca Keycloak girişi ile çalışır. Hub güncellenmeli veya yanlış sunucuya bağlanılıyor olabilirsiniz.";
    } else {
      applyAuthUi(cfg);
      loginPath = cfg.loginUrl ?? "/auth/login";
      logoutPath = cfg.logoutUrl ?? "/auth/logout";
      setWallSub("Durum kontrol ediliyor…");
    }
  } catch {
    hubUnavailable =
      "/api/auth/config ulaşılamıyor. Sayfayı hub adresinden açtığınızdan emin olun (ör. https://rmt.... veya geliştirmede http://localhost:8787).";
  }

  await reconcileAuthChrome();

  if (import.meta.env.DEV && !hubUnavailable) {
    const s = await fetchSession();
    if (!s.authenticated) {
      setWallSub(
        "Geliştirme: Keycloak çerezi için doğrudan hub kök URL’sini kullanın (http://localhost:8787)."
      );
    }
  }

  $("connect")?.addEventListener("click", wireSocket);
  $("new-session")?.addEventListener("click", openSession);

  window.addEventListener("focus", () => {
    void reconcileAuthChrome();
  });
}

void init();
